const translations = {
  en: {
    // Meta
    meta: {
      title: "Hungary's White Card | EU Residency for Remote Workers",
      description: "Get expert help with Hungary's White Card (Digital Nomad Visa). Eligibility check, application guidance, and fast support.",
      ogTitle: "Hungary's White Card Visa Assistance | Live & Work in Europe",
      ogDescription: "Get EU residency with Hungary's White Card. Live legally in Europe and travel across Schengen as a remote worker.",
    },

    // Header
    header: {
      logo: 'Residency Consulting',
      about: 'About',
      benefits: 'Benefits',
      pricing: 'Pricing',
      contactUs: 'Contact Us',
    },

    // Hero
    hero: {
      title: "Live and Work Legally in Europe with Hungary's White Card",
      subtitle: 'EU Residency for Remote Workers, Freelancers & Business Owners',
      description: 'Get legal EU residency, travel across the Schengen Area, and work remotely from Hungary with a simple and reliable process.',
      checkEligibility: 'Check Your Eligibility',
      bookConsultation: 'Book a Free Consultation',
    },

    // White Card section
    whiteCard: {
      title: 'What is the White Card of Hungary?',
      description: "The White Card of Hungary is a special residency permit designed for digital nomads, remote workers, and foreign entrepreneurs. It allows you to live legally in Europe while working remotely for foreign companies or running your own business.",
      facts: [
        { title: 'Residency up to 2 years', text: 'Long-term residence permit with renewal options' },
        { title: 'Minimum income €3,000/month', text: 'Stable income requirement for approval' },
        { title: 'Remote work or foreign business', text: 'Work for international companies or own business' },
        { title: 'Schengen visa-free travel', text: 'Travel freely across 27 European countries' },
      ],
    },

    // Benefits
    benefits: {
      title: 'Benefits of Hungary',
      items: [
        { title: 'Free Access to Schengen Area', text: 'Travel visa-free to 27 European countries with your Hungary residence permit' },
        { title: 'Affordable Cost of Living', text: 'Enjoy European quality of life at 40-50% lower costs than Western Europe' },
        { title: 'EU-Level Healthcare', text: 'Access high-quality healthcare services and facilities' },
        { title: 'Safe Country', text: 'One of the safest countries in Europe with low crime rates' },
        { title: 'Low Taxes (15%)', text: 'Competitive flat tax rate of 15% for personal income' },
        { title: 'Central European Location', text: 'Perfect base to explore Europe with excellent connectivity' },
      ],
    },

    // Eligibility
    eligibility: {
      title: 'Who is Eligible?',
      remote: {
        title: 'Remote Employees',
        description: 'You work remotely for a foreign company (non-Hungarian) with a stable employment contract. Your income must be at least €3,000 per month.',
        items: [
          'Employment contract with foreign company',
          'Minimum €3,000/month income',
          'Ability to work remotely from Hungary',
          'Non-EU citizenship',
        ],
      },
      business: {
        title: 'Foreign Business Owners',
        description: 'You own and operate a business registered outside of Hungary that generates stable income. Your business income must be at least €3,000 per month.',
        items: [
          'Own a foreign-registered business',
          'Minimum €3,000/month business income',
          'Business operates independently',
          'Non-EU citizenship',
        ],
      },
    },

    // Lifestyle
    lifestyle: {
      title: 'Why Digital Nomads Choose Hungary',
      description: "Hungary has become one of Europe's top destinations for digital nomads and remote workers. With its blend of rich history, vibrant culture, and modern infrastructure, Budapest offers the perfect environment for remote professionals.",
      items: [
        'Fast and reliable internet (average 100+ Mbps)',
        'Thriving coworking and cafe culture',
        'English widely spoken in major cities',
        'Rich cultural scene and nightlife',
        'Beautiful architecture and thermal baths',
        'Growing international community',
      ],
      imageAlt: 'Digital nomad lifestyle',
    },

    // Pricing
    pricing: {
      title: 'Full White Card Service',
      packageTitle: 'Complete Application Package',
      includedTitle: "What's Included:",
      items: [
        'Complete eligibility assessment',
        'Document preparation and review',
        'Application submission to authorities',
        'Appointment scheduling and coordination',
        'Translation of all required documents',
        'Legal representation throughout the process',
        'Address registration assistance',
        'Healthcare enrollment support',
        'Tax registration guidance',
        'Follow-up until permit is issued',
      ],
      cta: 'Get Started Today',
    },

    // About
    about: {
      title: 'About Us',
      text1: 'We are a specialized immigration consultancy based in Hungary, dedicated to helping international professionals and entrepreneurs navigate the Hungarian residency process. With years of experience in immigration law and a deep understanding of the White Card program, we provide comprehensive support from initial consultation to permit issuance.',
      text2: "Our team of legal experts and immigration specialists works closely with Hungarian authorities to ensure a smooth and efficient application process. We pride ourselves on our high success rate and personalized approach to each client's unique situation.",
      stats: [
        { number: '500+', label: 'Successful Applications' },
        { number: '95%', label: 'Success Rate' },
        { number: '50+', label: 'Countries Served' },
      ],
    },

    // Contact
    contact: {
      title: 'Get in Touch',
      infoTitle: 'Contact Information',
      emailLabel: 'Email',
      note: "Fill out the form and we'll get back to you within 24 hours to discuss your eligibility and next steps.",
      form: {
        fullName: 'Full Name *',
        fullNamePlaceholder: 'John Doe',
        email: 'Email *',
        emailPlaceholder: 'john@example.com',
        nationality: 'Nationality *',
        nationalityPlaceholder: 'United States',
        monthlyIncome: 'Monthly Income (EUR) *',
        monthlyIncomePlaceholder: '3000',
        employmentType: 'Are you an employee or business owner? *',
        employmentTypePlaceholder: 'Select your employment type',
        employeeOption: 'Remote Employee',
        businessOption: 'Business Owner',
        gdprLabel: 'I agree to the',
        gdprLink: 'Privacy Policy',
        gdprSuffix: 'and consent to my data being stored *',
        submit: 'Submit Application',
        submitting: 'Submitting...',
      },
      validation: {
        missingFields: 'Please fill in all required fields.',
        missingEmployment: 'Please select your employment type.',
        gdprRequired: 'You must agree to the Privacy Policy to submit.',
        success: 'Thank you! We will contact you within 24 hours.',
        error: 'Something went wrong. Please try again or email office@whitecard.hu',
      },
    },

    // Privacy
    privacy: {
      title: 'Privacy Policy',
      intro: 'We respect your privacy and are committed to protecting your personal data. By submitting the contact form on this website, you consent to:',
      items: [
        'Storage of your personal information for consultation and application processing purposes',
        "Email communication regarding your Hungary's White Card application inquiry",
        'Data processing in compliance with GDPR and applicable data protection regulations',
        'Secure storage of your information on our systems',
      ],
      rightsTitle: 'Your Rights:',
      rightsText: 'You have the right to access, rectify, or request deletion of your personal data at any time. Your data will never be shared with third parties without your explicit consent, except where required by law.',
      contactText: 'For data protection inquiries or to exercise your rights, please contact us at',
    },

    // Footer
    footer: {
      logo: 'Residency Consulting',
      tagline: 'Your trusted partner for Hungary immigration services',
      quickLinks: 'Quick Links',
      aboutLink: 'About Us',
      benefitsLink: 'Benefits',
      eligibilityLink: 'Eligibility',
      pricingLink: 'Pricing',
      contactTitle: 'Contact',
      copyright: '© 2025 Boldigital. All rights reserved.',
    },
  },

  ru: {
    // Meta
    meta: {
      title: 'Белая карта Венгрии | ВНЖ в ЕС для удалённых работников',
      description: 'Профессиональная помощь в получении Белой карты Венгрии (виза цифрового кочевника). Проверка соответствия требованиям, подготовка документов, быстрая консультация.',
      ogTitle: 'Белая карта Венгрии | Живите и работайте в Европе',
      ogDescription: 'Получите ВНЖ в Евросоюзе с Белой картой Венгрии. Легальное проживание в Европе и свободное перемещение по Шенгенской зоне.',
    },

    // Header
    header: {
      logo: 'Residency Consulting',
      about: 'О нас',
      benefits: 'Преимущества',
      pricing: 'Услуги',
      contactUs: 'Связаться',
    },

    // Hero
    hero: {
      title: 'Живите и работайте легально в Европе с Белой картой Венгрии',
      subtitle: 'ВНЖ в ЕС для удалённых работников, фрилансеров и предпринимателей',
      description: 'Получите легальный вид на жительство в ЕС, путешествуйте по Шенгенской зоне и работайте удалённо из Венгрии — просто и надёжно.',
      checkEligibility: 'Проверить соответствие',
      bookConsultation: 'Бесплатная консультация',
    },

    // White Card section
    whiteCard: {
      title: 'Что такое Белая карта Венгрии?',
      description: 'Белая карта Венгрии — это специальный вид на жительство для цифровых кочевников, удалённых работников и иностранных предпринимателей. Она позволяет легально жить в Европе, работая удалённо на зарубежные компании или ведя собственный бизнес.',
      facts: [
        { title: 'ВНЖ до 2 лет', text: 'Долгосрочный вид на жительство с возможностью продления' },
        { title: 'Минимальный доход €3 000/мес.', text: 'Требование стабильного дохода для одобрения' },
        { title: 'Удалённая работа или иностранный бизнес', text: 'Работа на международные компании или собственный бизнес' },
        { title: 'Безвизовый въезд в Шенген', text: 'Свободное перемещение по 27 странам Европы' },
      ],
    },

    // Benefits
    benefits: {
      title: 'Преимущества Венгрии',
      items: [
        { title: 'Свободный доступ в Шенгенскую зону', text: 'Безвизовые поездки по 27 странам Европы с видом на жительство в Венгрии' },
        { title: 'Доступная стоимость жизни', text: 'Европейское качество жизни при затратах на 40–50% ниже, чем в Западной Европе' },
        { title: 'Медицина европейского уровня', text: 'Доступ к качественным медицинским услугам и учреждениям' },
        { title: 'Безопасная страна', text: 'Одна из самых безопасных стран Европы с низким уровнем преступности' },
        { title: 'Низкие налоги (15%)', text: 'Конкурентная фиксированная ставка подоходного налога — 15%' },
        { title: 'Центральное расположение в Европе', text: 'Идеальная база для путешествий по Европе с отличным транспортным сообщением' },
      ],
    },

    // Eligibility
    eligibility: {
      title: 'Кто может подать заявку?',
      remote: {
        title: 'Удалённые сотрудники',
        description: 'Вы работаете удалённо на иностранную компанию (не венгерскую) по стабильному трудовому договору. Ваш доход должен составлять не менее €3 000 в месяц.',
        items: [
          'Трудовой договор с иностранной компанией',
          'Минимальный доход €3 000/мес.',
          'Возможность работать удалённо из Венгрии',
          'Гражданство не из стран ЕС',
        ],
      },
      business: {
        title: 'Владельцы иностранного бизнеса',
        description: 'Вы владеете и управляете бизнесом, зарегистрированным за пределами Венгрии, с стабильным доходом. Доход вашего бизнеса должен составлять не менее €3 000 в месяц.',
        items: [
          'Собственный бизнес, зарегистрированный за рубежом',
          'Минимальный доход бизнеса €3 000/мес.',
          'Бизнес функционирует самостоятельно',
          'Гражданство не из стран ЕС',
        ],
      },
    },

    // Lifestyle
    lifestyle: {
      title: 'Почему цифровые кочевники выбирают Венгрию',
      description: 'Венгрия стала одним из самых популярных направлений в Европе для цифровых кочевников и удалённых работников. Благодаря богатой истории, яркой культуре и современной инфраструктуре Будапешт — идеальное место для удалённых специалистов.',
      items: [
        'Быстрый и надёжный интернет (в среднем 100+ Мбит/с)',
        'Развитая культура коворкингов и кафе',
        'Английский широко распространён в крупных городах',
        'Богатая культурная жизнь и ночная жизнь',
        'Красивая архитектура и термальные купальни',
        'Растущее международное сообщество',
      ],
      imageAlt: 'Жизнь цифрового кочевника',
    },

    // Pricing
    pricing: {
      title: 'Полный пакет услуг «Белая карта»',
      packageTitle: 'Комплексный пакет оформления',
      includedTitle: 'Что включено:',
      items: [
        'Полная оценка соответствия требованиям',
        'Подготовка и проверка документов',
        'Подача заявления в государственные органы',
        'Запись на приём и координация',
        'Перевод всех необходимых документов',
        'Юридическое сопровождение на всех этапах',
        'Помощь с регистрацией адреса',
        'Помощь с оформлением медицинской страховки',
        'Консультации по налоговой регистрации',
        'Сопровождение до получения разрешения',
      ],
      cta: 'Начать оформление',
    },

    // About
    about: {
      title: 'О нас',
      text1: 'Мы — специализированная иммиграционная консалтинговая компания в Венгрии, помогающая международным специалистам и предпринимателям пройти процесс получения вида на жительство. Благодаря многолетнему опыту в иммиграционном праве и глубокому знанию программы «Белая карта» мы обеспечиваем комплексную поддержку от первой консультации до получения разрешения.',
      text2: 'Наша команда юристов и иммиграционных специалистов тесно сотрудничает с венгерскими властями для обеспечения гладкого и эффективного процесса подачи заявления. Мы гордимся высоким процентом успешных заявок и индивидуальным подходом к каждому клиенту.',
      stats: [
        { number: '500+', label: 'Успешных заявок' },
        { number: '95%', label: 'Процент одобрений' },
        { number: '50+', label: 'Стран обслуживания' },
      ],
    },

    // Contact
    contact: {
      title: 'Свяжитесь с нами',
      infoTitle: 'Контактная информация',
      emailLabel: 'Электронная почта',
      note: 'Заполните форму, и мы свяжемся с вами в течение 24 часов, чтобы обсудить ваше соответствие требованиям и дальнейшие шаги.',
      form: {
        fullName: 'Полное имя *',
        fullNamePlaceholder: 'Иван Иванов',
        email: 'Электронная почта *',
        emailPlaceholder: 'ivan@example.com',
        nationality: 'Гражданство *',
        nationalityPlaceholder: 'Россия',
        monthlyIncome: 'Ежемесячный доход (EUR) *',
        monthlyIncomePlaceholder: '3000',
        employmentType: 'Вы наёмный сотрудник или владелец бизнеса? *',
        employmentTypePlaceholder: 'Выберите тип занятости',
        employeeOption: 'Удалённый сотрудник',
        businessOption: 'Владелец бизнеса',
        gdprLabel: 'Я соглашаюсь с',
        gdprLink: 'Политикой конфиденциальности',
        gdprSuffix: 'и даю согласие на хранение моих данных *',
        submit: 'Отправить заявку',
        submitting: 'Отправка...',
      },
      validation: {
        missingFields: 'Пожалуйста, заполните все обязательные поля.',
        missingEmployment: 'Пожалуйста, выберите тип занятости.',
        gdprRequired: 'Необходимо согласиться с Политикой конфиденциальности.',
        success: 'Спасибо! Мы свяжемся с вами в течение 24 часов.',
        error: 'Произошла ошибка. Попробуйте ещё раз или напишите на office@whitecard.hu',
      },
    },

    // Privacy
    privacy: {
      title: 'Политика конфиденциальности',
      intro: 'Мы уважаем вашу конфиденциальность и обязуемся защищать ваши персональные данные. Отправляя контактную форму на этом сайте, вы даёте согласие на:',
      items: [
        'Хранение вашей персональной информации для целей консультации и оформления заявки',
        'Электронную переписку по вашему запросу о Белой карте Венгрии',
        'Обработку данных в соответствии с GDPR и применимыми нормами защиты данных',
        'Безопасное хранение вашей информации в наших системах',
      ],
      rightsTitle: 'Ваши права:',
      rightsText: 'Вы имеете право на доступ, исправление или удаление своих персональных данных в любое время. Ваши данные никогда не будут переданы третьим лицам без вашего явного согласия, за исключением случаев, предусмотренных законом.',
      contactText: 'По вопросам защиты данных или для реализации ваших прав, пожалуйста, свяжитесь с нами по адресу',
    },

    // Footer
    footer: {
      logo: 'Residency Consulting',
      tagline: 'Ваш надёжный партнёр по иммиграции в Венгрию',
      quickLinks: 'Быстрые ссылки',
      aboutLink: 'О нас',
      benefitsLink: 'Преимущества',
      eligibilityLink: 'Требования',
      pricingLink: 'Услуги',
      contactTitle: 'Контакты',
      copyright: '© 2025 Boldigital. Все права защищены.',
    },
  },

  ar: {
    // Meta
    meta: {
      title: 'البطاقة البيضاء في المجر | إقامة أوروبية للعاملين عن بُعد',
      description: 'احصل على مساعدة متخصصة للحصول على البطاقة البيضاء في المجر (تأشيرة الرحّالة الرقميين). تحقق من الأهلية، إرشاد في تقديم الطلب، ودعم سريع.',
      ogTitle: 'البطاقة البيضاء في المجر | عِش واعمل في أوروبا',
      ogDescription: 'احصل على إقامة في الاتحاد الأوروبي عبر البطاقة البيضاء في المجر. عِش بشكل قانوني في أوروبا وتنقّل بحرية في منطقة شنغن.',
    },

    // Header
    header: {
      logo: 'Residency Consulting',
      about: 'من نحن',
      benefits: 'المزايا',
      pricing: 'الخدمات',
      contactUs: 'تواصل معنا',
    },

    // Hero
    hero: {
      title: 'عِش واعمل بشكل قانوني في أوروبا مع البطاقة البيضاء في المجر',
      subtitle: 'إقامة أوروبية للعاملين عن بُعد والمستقلين وأصحاب الأعمال',
      description: 'احصل على إقامة قانونية في الاتحاد الأوروبي، وتنقّل عبر منطقة شنغن، واعمل عن بُعد من المجر بطريقة بسيطة وموثوقة.',
      checkEligibility: 'تحقق من أهليتك',
      bookConsultation: 'احجز استشارة مجانية',
    },

    // White Card section
    whiteCard: {
      title: 'ما هي البطاقة البيضاء في المجر؟',
      description: 'البطاقة البيضاء في المجر هي تصريح إقامة خاص مصمّم للرحّالة الرقميين والعاملين عن بُعد ورواد الأعمال الأجانب. تتيح لك العيش بشكل قانوني في أوروبا أثناء العمل عن بُعد لشركات أجنبية أو إدارة عملك الخاص.',
      facts: [
        { title: 'إقامة تصل إلى سنتين', text: 'تصريح إقامة طويل الأمد مع إمكانية التجديد' },
        { title: 'حد أدنى للدخل 3,000 يورو/شهرياً', text: 'متطلب دخل مستقر للموافقة' },
        { title: 'عمل عن بُعد أو عمل أجنبي', text: 'العمل لشركات دولية أو إدارة عملك الخاص' },
        { title: 'تنقّل حر في شنغن', text: 'السفر بحرية عبر 27 دولة أوروبية' },
      ],
    },

    // Benefits
    benefits: {
      title: 'مزايا المجر',
      items: [
        { title: 'حرية التنقل في منطقة شنغن', text: 'سافر بدون تأشيرة إلى 27 دولة أوروبية بتصريح إقامتك في المجر' },
        { title: 'تكلفة معيشة معقولة', text: 'استمتع بجودة الحياة الأوروبية بتكاليف أقل بنسبة 40-50% من غرب أوروبا' },
        { title: 'رعاية صحية بمستوى أوروبي', text: 'الوصول إلى خدمات ومرافق صحية عالية الجودة' },
        { title: 'بلد آمن', text: 'واحدة من أكثر الدول أماناً في أوروبا مع معدلات جريمة منخفضة' },
        { title: 'ضرائب منخفضة (15%)', text: 'معدل ضريبة دخل ثابت وتنافسي بنسبة 15%' },
        { title: 'موقع مركزي في أوروبا', text: 'قاعدة مثالية لاستكشاف أوروبا مع اتصالات ممتازة' },
      ],
    },

    // Eligibility
    eligibility: {
      title: 'من يمكنه التقديم؟',
      remote: {
        title: 'الموظفون عن بُعد',
        description: 'تعمل عن بُعد لشركة أجنبية (غير مجرية) بعقد عمل مستقر. يجب أن يكون دخلك 3,000 يورو على الأقل شهرياً.',
        items: [
          'عقد عمل مع شركة أجنبية',
          'حد أدنى للدخل 3,000 يورو/شهرياً',
          'القدرة على العمل عن بُعد من المجر',
          'جنسية من خارج الاتحاد الأوروبي',
        ],
      },
      business: {
        title: 'أصحاب الأعمال الأجنبية',
        description: 'تمتلك وتدير عملاً مسجلاً خارج المجر يدرّ دخلاً مستقراً. يجب أن يكون دخل عملك 3,000 يورو على الأقل شهرياً.',
        items: [
          'امتلاك عمل مسجل في الخارج',
          'حد أدنى لدخل العمل 3,000 يورو/شهرياً',
          'العمل يعمل بشكل مستقل',
          'جنسية من خارج الاتحاد الأوروبي',
        ],
      },
    },

    // Lifestyle
    lifestyle: {
      title: 'لماذا يختار الرحّالة الرقميون المجر',
      description: 'أصبحت المجر واحدة من أفضل الوجهات في أوروبا للرحّالة الرقميين والعاملين عن بُعد. بفضل تاريخها الغني وثقافتها النابضة وبنيتها التحتية الحديثة، توفر بودابست البيئة المثالية للمحترفين عن بُعد.',
      items: [
        'إنترنت سريع وموثوق (متوسط +100 ميغابت/ثانية)',
        'ثقافة مساحات العمل المشتركة والمقاهي المزدهرة',
        'الإنجليزية منتشرة في المدن الكبرى',
        'مشهد ثقافي غني وحياة ليلية',
        'عمارة جميلة وحمامات حرارية',
        'مجتمع دولي متنامٍ',
      ],
      imageAlt: 'نمط حياة الرحّالة الرقميين',
    },

    // Pricing
    pricing: {
      title: 'خدمة البطاقة البيضاء الكاملة',
      packageTitle: 'حزمة التقديم الشاملة',
      includedTitle: 'ما يتضمنه:',
      items: [
        'تقييم شامل للأهلية',
        'إعداد ومراجعة المستندات',
        'تقديم الطلب للسلطات',
        'جدولة المواعيد والتنسيق',
        'ترجمة جميع المستندات المطلوبة',
        'تمثيل قانوني طوال العملية',
        'المساعدة في تسجيل العنوان',
        'المساعدة في التسجيل الصحي',
        'إرشاد التسجيل الضريبي',
        'المتابعة حتى صدور التصريح',
      ],
      cta: 'ابدأ الآن',
    },

    // About
    about: {
      title: 'من نحن',
      text1: 'نحن شركة استشارات هجرة متخصصة مقرّها المجر، مكرّسة لمساعدة المحترفين الدوليين ورواد الأعمال في عملية الحصول على الإقامة المجرية. بفضل سنوات من الخبرة في قانون الهجرة وفهم عميق لبرنامج البطاقة البيضاء، نقدم دعماً شاملاً من الاستشارة الأولى حتى إصدار التصريح.',
      text2: 'يعمل فريقنا من الخبراء القانونيين والمتخصصين في الهجرة بشكل وثيق مع السلطات المجرية لضمان عملية تقديم سلسة وفعالة. نفخر بمعدل نجاحنا العالي ونهجنا الشخصي تجاه كل عميل.',
      stats: [
        { number: '+500', label: 'طلب ناجح' },
        { number: '95%', label: 'معدل النجاح' },
        { number: '+50', label: 'دولة تمّ خدمتها' },
      ],
    },

    // Contact
    contact: {
      title: 'تواصل معنا',
      infoTitle: 'معلومات الاتصال',
      emailLabel: 'البريد الإلكتروني',
      note: 'املأ النموذج وسنتواصل معك خلال 24 ساعة لمناقشة أهليتك والخطوات التالية.',
      form: {
        fullName: 'الاسم الكامل *',
        fullNamePlaceholder: 'أحمد محمد',
        email: 'البريد الإلكتروني *',
        emailPlaceholder: 'ahmed@example.com',
        nationality: 'الجنسية *',
        nationalityPlaceholder: 'مصر',
        monthlyIncome: 'الدخل الشهري (يورو) *',
        monthlyIncomePlaceholder: '3000',
        employmentType: 'هل أنت موظف أم صاحب عمل؟ *',
        employmentTypePlaceholder: 'اختر نوع التوظيف',
        employeeOption: 'موظف عن بُعد',
        businessOption: 'صاحب عمل',
        gdprLabel: 'أوافق على',
        gdprLink: 'سياسة الخصوصية',
        gdprSuffix: 'وأوافق على تخزين بياناتي *',
        submit: 'إرسال الطلب',
        submitting: 'جارٍ الإرسال...',
      },
      validation: {
        missingFields: 'يرجى ملء جميع الحقول المطلوبة.',
        missingEmployment: 'يرجى اختيار نوع التوظيف.',
        gdprRequired: 'يجب الموافقة على سياسة الخصوصية.',
        success: 'شكراً لك! سنتواصل معك خلال 24 ساعة.',
        error: 'حدث خطأ. يرجى المحاولة مرة أخرى أو مراسلتنا على office@whitecard.hu',
      },
    },

    // Privacy
    privacy: {
      title: 'سياسة الخصوصية',
      intro: 'نحترم خصوصيتك ونلتزم بحماية بياناتك الشخصية. بإرسال نموذج الاتصال على هذا الموقع، فإنك توافق على:',
      items: [
        'تخزين معلوماتك الشخصية لأغراض الاستشارة ومعالجة الطلب',
        'التواصل عبر البريد الإلكتروني بخصوص استفسارك عن البطاقة البيضاء في المجر',
        'معالجة البيانات وفقاً لنظام GDPR ولوائح حماية البيانات المعمول بها',
        'التخزين الآمن لمعلوماتك في أنظمتنا',
      ],
      rightsTitle: 'حقوقك:',
      rightsText: 'لديك الحق في الوصول إلى بياناتك الشخصية أو تصحيحها أو طلب حذفها في أي وقت. لن تتم مشاركة بياناتك مع أطراف ثالثة دون موافقتك الصريحة، باستثناء ما يقتضيه القانون.',
      contactText: 'لاستفسارات حماية البيانات أو لممارسة حقوقك، يرجى التواصل معنا على',
    },

    // Footer
    footer: {
      logo: 'Residency Consulting',
      tagline: 'شريكك الموثوق لخدمات الهجرة إلى المجر',
      quickLinks: 'روابط سريعة',
      aboutLink: 'من نحن',
      benefitsLink: 'المزايا',
      eligibilityLink: 'الأهلية',
      pricingLink: 'الخدمات',
      contactTitle: 'اتصل بنا',
      copyright: '© 2025 Boldigital. جميع الحقوق محفوظة.',
    },
  },

  es: {
    meta: {
      title: 'Tarjeta Blanca de Hungría | Residencia en la UE para trabajadores remotos',
      description: 'Obtén ayuda profesional con la Tarjeta Blanca de Hungría (Visa de Nómada Digital). Verificación de elegibilidad, orientación en la solicitud y soporte rápido.',
      ogTitle: 'Tarjeta Blanca de Hungría | Vive y trabaja en Europa',
      ogDescription: 'Obtén residencia en la UE con la Tarjeta Blanca de Hungría. Vive legalmente en Europa y viaja libremente por el espacio Schengen.',
    },

    header: {
      logo: 'Residency Consulting',
      about: 'Nosotros',
      benefits: 'Beneficios',
      pricing: 'Servicios',
      contactUs: 'Contáctanos',
    },

    hero: {
      title: 'Vive y trabaja legalmente en Europa con la Tarjeta Blanca de Hungría',
      subtitle: 'Residencia en la UE para trabajadores remotos, freelancers y empresarios',
      description: 'Obtén residencia legal en la UE, viaja por el espacio Schengen y trabaja de forma remota desde Hungría con un proceso sencillo y confiable.',
      checkEligibility: 'Verifica tu elegibilidad',
      bookConsultation: 'Consulta gratuita',
    },

    whiteCard: {
      title: '¿Qué es la Tarjeta Blanca de Hungría?',
      description: 'La Tarjeta Blanca de Hungría es un permiso de residencia especial diseñado para nómadas digitales, trabajadores remotos y emprendedores extranjeros. Te permite vivir legalmente en Europa mientras trabajas de forma remota para empresas extranjeras o gestionas tu propio negocio.',
      facts: [
        { title: 'Residencia hasta 2 años', text: 'Permiso de residencia a largo plazo con opción de renovación' },
        { title: 'Ingreso mínimo de €3.000/mes', text: 'Requisito de ingresos estables para la aprobación' },
        { title: 'Trabajo remoto o negocio extranjero', text: 'Trabaja para empresas internacionales o gestiona tu negocio' },
        { title: 'Viaje libre por Schengen', text: 'Viaja libremente por 27 países europeos' },
      ],
    },

    benefits: {
      title: 'Beneficios de Hungría',
      items: [
        { title: 'Acceso libre al espacio Schengen', text: 'Viaja sin visa a 27 países europeos con tu permiso de residencia húngaro' },
        { title: 'Costo de vida accesible', text: 'Disfruta de calidad de vida europea con costos 40-50% menores que en Europa occidental' },
        { title: 'Sanidad de nivel europeo', text: 'Acceso a servicios e instalaciones sanitarias de alta calidad' },
        { title: 'País seguro', text: 'Uno de los países más seguros de Europa con bajas tasas de criminalidad' },
        { title: 'Impuestos bajos (15%)', text: 'Tasa fija competitiva del 15% para el impuesto sobre la renta' },
        { title: 'Ubicación central en Europa', text: 'Base ideal para explorar Europa con excelentes conexiones' },
      ],
    },

    eligibility: {
      title: '¿Quién puede solicitarla?',
      remote: {
        title: 'Empleados remotos',
        description: 'Trabajas de forma remota para una empresa extranjera (no húngara) con un contrato de trabajo estable. Tu ingreso debe ser de al menos €3.000 al mes.',
        items: [
          'Contrato de trabajo con empresa extranjera',
          'Ingreso mínimo de €3.000/mes',
          'Capacidad de trabajar remotamente desde Hungría',
          'Nacionalidad fuera de la UE',
        ],
      },
      business: {
        title: 'Propietarios de negocios extranjeros',
        description: 'Posees y operas un negocio registrado fuera de Hungría que genera ingresos estables. Los ingresos de tu negocio deben ser de al menos €3.000 al mes.',
        items: [
          'Negocio propio registrado en el extranjero',
          'Ingreso mínimo del negocio de €3.000/mes',
          'El negocio opera de forma independiente',
          'Nacionalidad fuera de la UE',
        ],
      },
    },

    lifestyle: {
      title: 'Por qué los nómadas digitales eligen Hungría',
      description: 'Hungría se ha convertido en uno de los destinos favoritos en Europa para nómadas digitales y trabajadores remotos. Con su rica historia, cultura vibrante e infraestructura moderna, Budapest ofrece el entorno perfecto para profesionales remotos.',
      items: [
        'Internet rápido y confiable (promedio de +100 Mbps)',
        'Cultura floreciente de coworking y cafeterías',
        'Inglés ampliamente hablado en las principales ciudades',
        'Rica escena cultural y vida nocturna',
        'Hermosa arquitectura y baños termales',
        'Comunidad internacional en crecimiento',
      ],
      imageAlt: 'Estilo de vida nómada digital',
    },

    pricing: {
      title: 'Servicio completo de Tarjeta Blanca',
      packageTitle: 'Paquete completo de solicitud',
      includedTitle: '¿Qué incluye?',
      items: [
        'Evaluación completa de elegibilidad',
        'Preparación y revisión de documentos',
        'Presentación de la solicitud ante las autoridades',
        'Programación y coordinación de citas',
        'Traducción de todos los documentos requeridos',
        'Representación legal durante todo el proceso',
        'Asistencia con el registro de dirección',
        'Asistencia con la inscripción sanitaria',
        'Orientación en el registro fiscal',
        'Seguimiento hasta la emisión del permiso',
      ],
      cta: 'Comienza ahora',
    },

    about: {
      title: 'Sobre nosotros',
      text1: 'Somos una consultora de inmigración especializada con sede en Hungría, dedicada a ayudar a profesionales internacionales y emprendedores a navegar el proceso de residencia húngara. Con años de experiencia en derecho migratorio y un profundo conocimiento del programa de Tarjeta Blanca, brindamos apoyo integral desde la consulta inicial hasta la emisión del permiso.',
      text2: 'Nuestro equipo de expertos legales y especialistas en inmigración trabaja estrechamente con las autoridades húngaras para garantizar un proceso de solicitud fluido y eficiente. Nos enorgullecemos de nuestra alta tasa de éxito y nuestro enfoque personalizado para cada cliente.',
      stats: [
        { number: '500+', label: 'Solicitudes exitosas' },
        { number: '95%', label: 'Tasa de éxito' },
        { number: '50+', label: 'Países atendidos' },
      ],
    },

    contact: {
      title: 'Contáctanos',
      infoTitle: 'Información de contacto',
      emailLabel: 'Correo electrónico',
      note: 'Completa el formulario y nos pondremos en contacto contigo en un plazo de 24 horas para analizar tu elegibilidad y los próximos pasos.',
      form: {
        fullName: 'Nombre completo *',
        fullNamePlaceholder: 'Juan García',
        email: 'Correo electrónico *',
        emailPlaceholder: 'juan@example.com',
        nationality: 'Nacionalidad *',
        nationalityPlaceholder: 'España',
        monthlyIncome: 'Ingreso mensual (EUR) *',
        monthlyIncomePlaceholder: '3000',
        employmentType: '¿Eres empleado o empresario? *',
        employmentTypePlaceholder: 'Selecciona tu tipo de empleo',
        employeeOption: 'Empleado remoto',
        businessOption: 'Empresario',
        gdprLabel: 'Acepto la',
        gdprLink: 'Política de Privacidad',
        gdprSuffix: 'y consiento el almacenamiento de mis datos *',
        submit: 'Enviar solicitud',
        submitting: 'Enviando...',
      },
      validation: {
        missingFields: 'Por favor, completa todos los campos obligatorios.',
        missingEmployment: 'Por favor, selecciona tu tipo de empleo.',
        gdprRequired: 'Debes aceptar la Política de Privacidad.',
        success: '¡Gracias! Nos pondremos en contacto contigo en 24 horas.',
        error: 'Algo salió mal. Inténtalo de nuevo o escríbenos a office@whitecard.hu',
      },
    },

    privacy: {
      title: 'Política de Privacidad',
      intro: 'Respetamos tu privacidad y nos comprometemos a proteger tus datos personales. Al enviar el formulario de contacto en este sitio, consientes:',
      items: [
        'El almacenamiento de tu información personal para fines de consulta y procesamiento de solicitudes',
        'La comunicación por correo electrónico sobre tu consulta de la Tarjeta Blanca de Hungría',
        'El procesamiento de datos conforme al RGPD y las normativas de protección de datos aplicables',
        'El almacenamiento seguro de tu información en nuestros sistemas',
      ],
      rightsTitle: 'Tus derechos:',
      rightsText: 'Tienes derecho a acceder, rectificar o solicitar la eliminación de tus datos personales en cualquier momento. Tus datos nunca serán compartidos con terceros sin tu consentimiento explícito, salvo cuando lo exija la ley.',
      contactText: 'Para consultas sobre protección de datos o para ejercer tus derechos, contáctanos en',
    },

    footer: {
      logo: 'Residency Consulting',
      tagline: 'Tu socio de confianza para servicios de inmigración a Hungría',
      quickLinks: 'Enlaces rápidos',
      aboutLink: 'Sobre nosotros',
      benefitsLink: 'Beneficios',
      eligibilityLink: 'Elegibilidad',
      pricingLink: 'Servicios',
      contactTitle: 'Contacto',
      copyright: '© 2025 Boldigital. Todos los derechos reservados.',
    },
  },
};

export default translations;
